import type { LocationDto } from "../../models/Locations";

/** ---- Types ---- */
export type LocationNode = LocationDto & { children: LocationNode[] };
export type FlatWithLevel = LocationDto & { level: number };

export function buildTree(rows: LocationDto[]): LocationNode[] {
    const byId = new Map<number, LocationNode>();
    const roots: LocationNode[] = [];

    for (const r of rows) byId.set(r.locationId, { ...r, children: [] });

    for (const n of byId.values()) {
        if (n.parentLocationId == null || !byId.has(n.parentLocationId)) {
            roots.push(n);
        } else {
            byId.get(n.parentLocationId)!.children.push(n);
        }
    }

    const sortTree = (node: LocationNode) => {
        node.children.sort((a, b) => a.locationName.localeCompare(b.locationName));
        node.children.forEach(sortTree);
    };
    roots.sort((a, b) => a.locationName.localeCompare(b.locationName));
    roots.forEach(sortTree);

    return roots;
}

/** ---- Flatten the tree with a 'level' depth (used by search/select fallbacks) ---- */
export function flattenWithLevel(nodes: LocationNode[], level = 0): FlatWithLevel[] {
    const out: FlatWithLevel[] = [];
    for (const n of nodes) {
        out.push({
            locationId: n.locationId,
            locationName: n.locationName,
            locationTypeId: n.locationTypeId,
            parentLocationId: n.parentLocationId,
            level,
        });
        if (n.children.length) out.push(...flattenWithLevel(n.children, level + 1));
    }
    return out;
}

/** ---- Indented label (kept for the compact "move" combobox fallback) ---- */
const NBSP = " ";
const INDENT = NBSP.repeat(2);
export function formatIndentedLabel(name: string, level: number) {
    return level === 0 ? name : `${INDENT.repeat(level)}▸ ${name}`;
}

/**
 * ---- Ancestor path for a node, root-first ----
 * Powers the breadcrumb trail in the details panel, e.g.
 * "UK ▸ North Region ▸ Leeds Store".
 */
export function getPath(rows: LocationDto[], locationId: number | null): LocationDto[] {
    if (locationId == null) return [];
    const byId = new Map(rows.map((r) => [r.locationId, r]));
    const path: LocationDto[] = [];
    let current = byId.get(locationId);
    const seen = new Set<number>();
    while (current && !seen.has(current.locationId)) {
        path.unshift(current);
        seen.add(current.locationId);
        current = current.parentLocationId == null ? undefined : byId.get(current.parentLocationId);
    }
    return path;
}

/** ---- Count all descendants of a node (for delete warnings) ---- */
export function countDescendants(rows: LocationDto[], locationId: number): number {
    const children = new Map<number, LocationDto[]>();
    for (const r of rows) {
        if (r.parentLocationId == null) continue;
        const list = children.get(r.parentLocationId) ?? [];
        list.push(r);
        children.set(r.parentLocationId, list);
    }
    let count = 0;
    const stack = [...(children.get(locationId) ?? [])];
    while (stack.length) {
        const next = stack.pop()!;
        count++;
        stack.push(...(children.get(next.locationId) ?? []));
    }
    return count;
}

/**
 * ---- Is `candidateId` the same node as, or a descendant of, `nodeId`? ----
 * Used to block drag-and-drop moves that would create a cycle
 * (dropping a location onto one of its own children).
 */
export function isSameOrDescendant(rows: LocationDto[], nodeId: number, candidateId: number): boolean {
    if (nodeId === candidateId) return true;
    const byId = new Map(rows.map((r) => [r.locationId, r]));
    let current = byId.get(candidateId);
    const seen = new Set<number>();
    while (current && !seen.has(current.locationId)) {
        if (current.parentLocationId === nodeId) return true;
        seen.add(current.locationId);
        current = current.parentLocationId == null ? undefined : byId.get(current.parentLocationId);
    }
    return false;
}

/**
 * ---- Prune the tree to nodes matching `query`, keeping ancestors of any match ----
 * so the hierarchy stays intact while searching. Returns the pruned tree
 * plus the set of node ids that should be force-expanded to reveal matches.
 */
export function filterTree(nodes: LocationNode[], query: string): { tree: LocationNode[]; expandIds: Set<number> } {
    const q = query.trim().toLowerCase();
    const expandIds = new Set<number>();
    if (!q) return { tree: nodes, expandIds };

    const walk = (list: LocationNode[]): LocationNode[] => {
        const result: LocationNode[] = [];
        for (const n of list) {
            const selfMatch = n.locationName.toLowerCase().includes(q);
            const filteredChildren = walk(n.children);
            if (selfMatch || filteredChildren.length) {
                result.push({ ...n, children: filteredChildren });
                if (filteredChildren.length) expandIds.add(n.locationId);
            }
        }
        return result;
    };

    return { tree: walk(nodes), expandIds };
}
